import { storefront } from '../client';
import { flattenMetaobjectResponse } from '../../utilities/storefront-api/flattenMetaobjects';
import { addGid } from '../../utilities/handleGid';
import { logError } from '../../utilities/logging';

const getProductVariantsQuery = `
query getProductVariantsQuery($id: ID!, $country: CountryCode)
@inContext(country: $country) {
  product(id: $id) {
    baseProductStepData: metafield(namespace: "$app:personalisation", key: "personalisationBaseProductConfig") {
      value
      reference {
        ... on Metaobject {
          id
          handle
          fields {
            key
            value
            reference {
              ... on Page {
                title
                body
                modalTriggerText: metafield(namespace: "$app:modal", key: "triggerText") {
                  value
                }
              }
            }
          }
        }
      }
    }
    variants(first: 250) {
      nodes {
        id
        title
        product {
          title
        }
        availableForSale
        
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
        image {
          url
        }
        selectedOptions {
          name
          value
        }
        product {
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
        }
        shortDescription: metafield(namespace: "$app:personalisation", key: "shortDescription") {
          value
        }
      }
    }
  }
}
`;

const getProductPersonalisationReferenceQuery = `
query getProductPersonalisationReferenceQuery($id: ID!, $country: CountryCode)
@inContext(country: $country) {
  product(id: $id) {
    personalisationReference: metafield(namespace: "$app:personalisation", key: "reference") {
      value
      reference {
        ... on Metaobject {
          id
          handle
          fields {
            key
            value
          }
        }
      }
    }
  }
}
`;
/**
 * Fetches personalisation data for a product.
 * @param {Object} options - Options object.
 * @param {string} options.productId - The ID of the product to fetch personalisation data for.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<Object>} - An object containing the personalisation response.
 */
export const fetchPersonalisationByProductId = async ({ productId, country }) => {
  const productGid = addGid({ id: productId, dataType: 'Product' });
  const { data, errors } = await storefront.request(getProductPersonalisationReferenceQuery, {
    variables: {
      id: productGid,
      country
    }
  });

  // Throw if we need to
  if (errors) {
    logError({
      label: 'fetchPersonalisationByProductId',
      error: new Error('GraphQL errors occurred'),
      message: 'Error gathering personalisation data for this product',
      extraData: { productId, country, errors }
    });
    throw new Error('Error gathering personalisation data for this product.');
  }

  // Extract and flatten the metaobject reference if present.
  const metaobject = data?.product?.personalisationReference?.reference;
  if (metaobject) {
    return flattenMetaobjectResponse({ metaobject });
  }
  return null;
};

/**
 * Fetches variants for a product.
 * @param {Object} options - Options object.
 * @param {string} options.productId - The ID of the product to fetch variants for.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<{variants: Array<Object>}>} - An object containing the variants.
 */
export const fetchVariantsByProductId = async ({ productId, country }) => {
  const productGid = addGid({ id: productId, dataType: 'Product' });

  const { data, errors } = await storefront.request(getProductVariantsQuery, {
    variables: {
      id: productGid,
      country
    }
  });

  if (errors) {
    logError({
      label: 'fetchVariantsByProductId',
      error: new Error('GraphQL errors occurred'),
      message: 'Error gathering variants for this product',
      extraData: { productId, country, errors }
    });
  }

  return { variants: data?.product?.variants?.nodes || [] };
};

/**
 * Fetches metaobject data for a product by metafield key.
 * @param {Object} options - Options object.
 * @param {string} options.productId - The ID of the product to fetch metaobject data for.
 * @param {string} options.metafieldKey - The metafield key to fetch data from.
 * @returns {Promise<Object|null>} - An object containing the metaobject data or null if not found.
 */
export const fetchMetaobjectByMetafieldKey = async ({ productId, metafieldKey }) => {
  const productGid = addGid({ id: productId, dataType: 'Product' });
  const { data, errors } = await storefront.request(getProductVariantsQuery, {
    variables: {
      id: productGid
    }
  });

  // Throw if we need to
  if (errors) {
    logError({
      label: 'fetchMetaobjectByMetafieldKey',
      error: new Error('GraphQL errors occurred'),
      message: 'Error gathering metaobject data for metafield key',
      extraData: { productId, metafieldKey, errors }
    });
    throw new Error(`Error gathering metaobject data for metafield key: ${metafieldKey}`);
  }

  // Extract and flatten the metaobject reference if present.
  const metaobject = data?.product?.[`${metafieldKey}`]?.reference;
  if (metaobject) {
    return flattenMetaobjectResponse({ metaobject });
  }
  return null;
};
